import React, { useState } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import ConfirmModal from '../components/shared/ConfirmModal';
import UserForm from './UserForm';
import { deleteUser, deactivateUser } from './userService';
import ActionDrawer from '../components/shared/ActionDrawer';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showServerError, showSuccess, showError } from '../../utils/toastHelper';

function UserManagement() {
  const { user: currentUser } = useAuth();
  const [selectedUser, setSelectedUser] = useState(null);
  const {
    isOpen: isUserOpen,
    open: openUser,
    close: closeUser
  } = useModal();
  const [processingIds, setProcessingIds] = useState({
    delete: [],
    deactivate: []
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: users,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    filters,
    refresh
  } = useDataTable('/admin/users', {
    initialFilters: {
      search: '',
      role: ''
    }
  });

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setActionLoading(true);
      setProcessingIds(prev => ({
        ...prev,
        delete: [...prev.delete, userToDelete]
      }));
      await deleteUser(userToDelete);
      showSuccess('Kullanıcı başarıyla silindi');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      refresh();
    } catch (error) {
      console.error('Failed to delete user:', error);
      showError(error.message || 'Kullanıcı silinirken bir hata oluştu');
    } finally {
      setActionLoading(false);
      setProcessingIds(prev => ({
        ...prev,
        delete: prev.delete.filter(id => id !== userToDelete)
      }));
    }
  };

  const handleDeactivate = (userId) => {
    setUserToDeactivate(userId);
    setShowDeactivateConfirm(true);
  };

  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      setActionLoading(true);
      setProcessingIds(prev => ({
        ...prev,
        deactivate: [...prev.deactivate, userToDeactivate]
      }));
      await deactivateUser(userToDeactivate);
      showSuccess('Kullanıcı başarıyla deaktif edildi');
      setShowDeactivateConfirm(false);
      setUserToDeactivate(null);
      refresh();
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Kullanıcı deaktif edilirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
      setProcessingIds(prev => ({
        ...prev,
        deactivate: prev.deactivate.filter(id => id !== userToDeactivate)
      }));
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    openUser();
  };

  const handleAdd = () => {
    setSelectedUser(null);
    openUser();
  };

  const handleUserSave = () => {
    refresh();
    closeUser();
    setSelectedUser(null);
  };

  const handleSendResetLink = async (email) => {
    try {
      await apiRequest('/auth/request-password-reset', {
        method: 'POST',
        data: { email },
        skipAuth: true
      });
      showSuccess(`${email} adresine şifre sıfırlama linki gönderildi.`);
    } catch (error) {
      console.error('Password reset link error:', error);
      showError(error.message || 'Şifre sıfırlama linki gönderilirken bir hata oluştu.');
    }
  };

  const handleClearFilters = () => {
    handleFilterChange('search', '');
    handleFilterChange('role', '');
  };

  const getRoleText = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Süper Admin';
      case 'COMPANY_ADMIN':
        return 'Şirket Yöneticisi';
      case 'SHIPPING_STAFF':
        return 'Kargo Personeli';
      case 'CUSTOMER':
        return 'Müşteri';
      default:
        return role || 'Bilinmeyen Rol';
    }
  };

  // Columns Configuration
  const columns = [
    {
      header: 'Ad Soyad',
      accessor: 'fullName',
      render: (row) => (
        <div className="d-flex align-items-center">
          <div className="bg-light rounded p-2 me-3">
            <i className="fas fa-user text-muted"></i>
          </div>
          <div>
            <h6 className="mb-0 text-truncate">
              {row.fullName}
              {Boolean(row.isCurrentUser) && (
                <small className="text-orange ms-2">(Siz)</small>
              )}
            </h6>
          </div>
        </div>
      )
    },
    {
      header: 'E-posta',
      accessor: 'email',
      render: (row) => (
        <span className="text-muted text-truncate d-inline-block">
          {row.email}
        </span>
      )
    },
    {
      header: 'Rol',
      accessor: 'role',
      render: (row) => (
        <span className="role-badge">{getRoleText(row.role)}</span>
      )
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} />
    },
    ...(currentUser?.role === 'SUPER_ADMIN' ? [{
      header: 'Şirket',
      accessor: 'company',
      render: (row) => (
        <span className="text-truncate d-inline-block">
          {row.company?.name || '-'}
        </span>
      )
    }] : []),
    {
      header: 'İşlemler',
      accessor: 'actions',
      render: (row) => (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleEdit(row)}>
                <i className="fas fa-edit fa-fw me-2"></i> Düzenle
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => handleSendResetLink(row.email)}>
                <i className="fas fa-key fa-fw me-2"></i> Şifre Sıfırlama Linki Gönder
              </button>
            </li>
            {!row.isCurrentUser && row.status !== 'INACTIVE' && (
              <li>
                <button className="dropdown-item text-danger" onClick={() => handleDeactivate(row.id)}>
                  <i className="fas fa-ban fa-fw me-2"></i> Deaktif Et
                </button>
              </li>
            )}
            {!row.isCurrentUser && (
              <li>
                <button className="dropdown-item text-danger" onClick={() => handleDelete(row.id)}>
                  <i className="fas fa-trash fa-fw me-2"></i> Sil
                </button>
              </li>
            )}
          </ul>
        </div>
      )
    }
  ];

  // Show full page loader on initial load
  if (loading && (!users || users.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Kullanıcılar yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Personel Yönetimi"
        description="Tüm personelleri buradan yönetebilirsiniz."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Kullanıcılar' }]}
        actions={
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i> Yeni Kullanıcı
          </button>
        }
      />

      {/* Filters */}
      <FilterBar
        filters={[
          {
            type: 'select',
            key: 'role',
            label: 'Rol',
            defaultValue: '',
            options: [
              { value: '', label: 'Tüm Roller' },
              { value: 'SUPER_ADMIN', label: 'Süper Admin' },
              { value: 'COMPANY_ADMIN', label: 'Şirket Yöneticisi' },
              { value: 'SHIPPING_STAFF', label: 'Kargo Personeli' },
              { value: 'CUSTOMER', label: 'Müşteri' }
            ]
          }
        ]}
        activeFilters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchPlaceholder="Ad veya e-posta ile ara..."
        showSearch={true}
        showClearButton={true}
      />

      {/* Content */}
      <DataTable
        tableId="users"
        columns={columns}
        data={users}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={(row) => !Boolean(row.isCurrentUser) && handleEdit(row)}
        emptyState={{
          title: 'Personel Bulunamadı',
          message: 'Gösterilecek personel bulunmuyor.',
          icon: 'user-slash'
        }}
      />

      {/* User Drawer */}
      <UserForm
        isOpen={isUserOpen}
        onClose={closeUser}
        user={selectedUser}
        onSuccess={() => {
          handleUserSave();
        }}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Kullanıcıyı Sil"
        message="Bu personeli silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={actionLoading}
      />

      <ConfirmModal
        show={showDeactivateConfirm}
        onClose={() => {
          setShowDeactivateConfirm(false);
          setUserToDeactivate(null);
        }}
        onConfirm={confirmDeactivate}
        title="Kullanıcıyı Deaktif Et"
        message="Bu kullanıcıyı deaktif etmek istediğinize emin misiniz?"
        confirmText="Deaktif Et"
        cancelText="İptal"
        variant="warning"
        loading={actionLoading}
      />

      <style>{`
        .role-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          line-height: 1;
          color: #495057;
          background-color: #f8f9fa;
          border-radius: 0.25rem;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}

export default UserManagement; 